import axios from 'axios';
import { getConfig } from '../client/config';
/**
 * Resolve the refresh/revoke base URL to a CONFIGURED issuer only. The session's
 * `issuer` is persisted in client storage; if that storage is tampered (e.g. XSS)
 * a raw value could redirect the refresh token to an attacker host. So we accept the
 * stored issuer only when it exactly matches a configured allowlisted issuer
 * (authApiUrl / humanAuthApiUrl); otherwise we fall back to authApiUrl. The refresh
 * token is therefore never POSTed anywhere except a Bounded-configured origin.
 */
function resolveIssuerBase(issuer, cfg) {
    const norm = (u) => (u || '').replace(/\/$/, '');
    const allow = [norm(cfg.authApiUrl), norm(cfg.humanAuthApiUrl)].filter(Boolean);
    const want = norm(issuer);
    if (want && allow.includes(want))
        return want;
    return norm(cfg.authApiUrl);
}
// #062: key the auth client by its baseURL. The old single memoized client froze the FIRST
// environment's authApiUrl, so after re-initializing the SDK to a different issuer (staging->
// prod, or a custom issuer) the mint calls (/nonce, /auth/nonce, /session) kept hitting the
// original address forever - a "works only after a full reload" login bug, and in a multi-tenant
// process one config's address could leak into another's. refresh/revoke already resolve per
// call via resolveIssuerBase; this makes minting do the same while still caching per URL.
const axiosClientsByUrl = new Map();
async function getAxiosAuthClient(baseURLOverride) {
    // A wallet client passes its snapshotted issuer so the mint never follows the
    // mutable global; the default path reads the current global config.
    const baseURL = baseURLOverride || (await getConfig()).authApiUrl || '';
    let client = axiosClientsByUrl.get(baseURL);
    if (!client) {
        client = axios.create({
            baseURL,
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 30000, // 30s timeout, matching makeApiRequest
        });
        axiosClientsByUrl.set(baseURL, client);
    }
    return client;
}
export async function genNonce() {
    const client = await getAxiosAuthClient();
    const response = await client.post('/nonce');
    return response.data;
}
export async function genAuthNonce(sessionConfig) {
    // Bind the nonce to the snapshotted app+issuer when a wallet client supplies
    // one; otherwise resolve from the mutable global config (unchanged default).
    const config = sessionConfig !== null && sessionConfig !== void 0 ? sessionConfig : await getConfig();
    const client = await getAxiosAuthClient(config.authApiUrl);
    const appId = config.appId;
    const response = await client.post('/auth/nonce', {
        appId
    });
    return response.data.nonce;
}
// authMethods that the /session issuer treats as a Solana wallet-signature flow.
// createSessionWithSignature ALWAYS exchanges an Ed25519 signature over the
// canonical genSolanaMessage prompt (server keypair, Phantom, mobile wallet,
// guest device key), so the method it reports must be a wallet method — never a
// human-login method like 'email' (the SDK default). Both issuers reject a
// signature carrying a non-wallet authMethod: the bounded-auth worker returns
// 501 ("email-OTP not yet enabled") and the legacy session-management server
// returns 400 ("EVM/SIWE auth is no longer supported"). See routes.ts /session.
// Wallet/signature methods whose authMethod is forwarded verbatim to the /session
// issuer. 'guest' is included so anonymous (device-keypair) logins arrive as
// authMethod:"guest" — the issuer gates them on the app's anonymous opt-in and
// stamps an is_anonymous claim. Without this a guest would be indistinguishable
// from 'phantom' and couldn't be gated or flagged.
const SIGNATURE_AUTH_METHODS = new Set(['phantom', 'mobile-wallet-adapter', 'guest']);
// DS2-0110: header a SERVER-SIDE caller (Node: the @bounded-sh/server keypair SDK, the CLI, an
// agent) presents so the wallet issuer's origin gate admits its ORIGIN-LESS /session mint. A
// non-browser client sends no Origin, and the issuer (AS-03) no longer treats a missing Origin as
// "server, allow"; the origin-less path is reserved for callers that PROVE a server-client
// credential via X-Bounded-Server-Auth, matched against the worker's SERVER_CLIENT_SECRET. We read
// it from the SAME env var Bind injects on the worker side (BOUNDED_SERVER_CLIENT_SECRET). Guarded
// for non-Node hosts - browsers never carry it and don't need it (they authenticate by Origin) -
// mirroring genSolanaMessage's process guard. Absent/empty => no header => the browser path is
// unchanged.
function serverAuthHeaders() {
    var _a;
    const secret = (typeof process !== 'undefined' && ((_a = process.env) === null || _a === void 0 ? void 0 : _a.BOUNDED_SERVER_CLIENT_SECRET)) || '';
    return secret ? { 'X-Bounded-Server-Auth': secret } : {};
}
// Maps a client authMethod to the /session issuer's signature discriminator when
// they differ. EVM WALLET LOGIN (config.authMethod 'evm-wallet') exchanges a SIWE
// (EIP-4361 personal_sign, secp256k1) signature, which the bounded-auth issuer
// routes on authMethod:"siwe" (index.ts /session → parseSiweMessage/recoverSiweSigner)
// and mints under custom:evmWalletAddress. The EVM provider also passes 'siwe'
// explicitly (authMethodOverride), so this map is the belt-and-suspenders path.
const SESSION_METHOD_ALIASES = { 'evm-wallet': 'siwe' };
/**
 * Exchange a wallet signature for a Bounded session at the /session issuer.
 *
 * @param authMethodOverride the exact /session discriminator to send (e.g. 'siwe'
 *   for the SIWE/EVM path). When omitted the method is derived from config: an
 *   aliased method (evm-wallet → siwe) maps through SESSION_METHOD_ALIASES, a
 *   Solana wallet/guest method passes through verbatim, and anything else
 *   defaults to the canonical 'phantom' the issuer accepts for a signature.
 */
export async function createSessionWithSignature(address, message, signature, authMethodOverride, sessionConfig) {
    var _a, _b, _c;
    // Bind the /session mint to the snapshotted app+issuer when a wallet client
    // supplies one; otherwise resolve from the mutable global (unchanged default).
    const config = sessionConfig !== null && sessionConfig !== void 0 ? sessionConfig : await getConfig();
    const client = await getAxiosAuthClient(config.authApiUrl);
    const appId = config.appId;
    // Preserve a wallet authMethod when one is configured (so the backend records
    // the real method, e.g. mobile-wallet-adapter); otherwise default to the
    // canonical Solana wallet method 'phantom' that every /session issuer accepts
    // for a signature exchange. This is what makes the server SDK + guest device
    // keypair work under the email-default config.
    const authMethod = (_a = authMethodOverride !== null && authMethodOverride !== void 0 ? authMethodOverride : SESSION_METHOD_ALIASES[config.authMethod]) !== null && _a !== void 0 ? _a : (SIGNATURE_AUTH_METHODS.has(config.authMethod) ? config.authMethod : 'phantom');
    try {
        const response = await client.post('/session', {
            appId,
            address,
            message,
            signature,
            authMethod
        }, { headers: serverAuthHeaders() });
        return response.data;
    }
    catch (err) {
        // Surface the issuer's actionable message (e.g. "Anonymous auth is not enabled for
        // this app. Add \"auth\": { \"anonymous\": true } to policy.json and redeploy.")
        // instead of axios's generic "Request failed with status code 403".
        const d = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data;
        if (d && (d.message || d.error)) {
            const e = new Error(String(d.message || d.error));
            e.code = d.error;
            e.status = (_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.status;
            e.hint = d.hint;
            throw e;
        }
        throw err;
    }
}
// Deduplicate concurrent refreshSession calls to prevent multiple code paths
// (WebSocket reconnection, periodic refresh, API 401 retry) from all hitting
// /session/refresh simultaneously.
//
// Keyed PER (issuer, token) in a Map, not one global slot (DS2-0373): the rotating
// refresh token is single-use, so two concurrent refreshes of the SAME token must
// share one in-flight request. A single global slot could hold only one token at a
// time, so an interleaved refresh of a DIFFERENT session's token evicted the first
// token's in-flight promise; a later second refresh of the first token then missed
// its own in-flight request and re-POSTed the already-spent token - the issuer sees
// reuse and revokes the whole family. Keying by token lets unrelated sessions
// coalesce independently.
// Note: module-level state is shared across requests in SSR/Node - acceptable since
// each key is bound to a specific session's token (same pattern as api.ts).
const refreshInFlightByToken = new Map();
export async function refreshSession(refreshToken, issuer) {
    const dedupeKey = `${issuer || ''}::${refreshToken}`;
    const existing = refreshInFlightByToken.get(dedupeKey);
    if (existing)
        return existing;
    let currentFlight;
    currentFlight = (async () => {
        try {
            const config = await getConfig();
            const appId = config.appId;
            // Route to the issuer that MINTED this session (email/social ->
            // humanAuthApiUrl, wallet/guest -> authApiUrl), validated against the
            // configured allowlist so a tampered stored issuer can't exfiltrate the token.
            const base = resolveIssuerBase(issuer, config);
            const response = await axios.post(`${base}/session/refresh`, {
                refreshToken,
                appId
            }, { headers: { 'Content-Type': 'application/json' }, timeout: 30000 });
            return response.data;
        }
        finally {
            // Only remove our own entry, and only if it is still the active flight
            // for this token (a later refresh of the same token cannot have replaced
            // it while we were in flight, but this stays correct if that ever changes).
            if (refreshInFlightByToken.get(dedupeKey) === currentFlight) {
                refreshInFlightByToken.delete(dedupeKey);
            }
        }
    })();
    refreshInFlightByToken.set(dedupeKey, currentFlight);
    return currentFlight;
}
export class SessionRevokeError extends Error {
    constructor(message, cause) {
        super(message);
        this.name = 'SessionRevokeError';
        this.cause = cause;
    }
}
function revokeFailureMessage(err) {
    var _a, _b;
    const status = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status;
    const statusText = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.statusText;
    const suffix = typeof status === 'number'
        ? ` (HTTP ${status}${statusText ? ` ${statusText}` : ''})`
        : '';
    return `Failed to revoke refresh token server-side${suffix}. The refresh-token family may still be active.`;
}
/**
 * Revoke a session's refresh-token family server-side (logout). Routes to the
 * minting issuer and rejects if the revoke request fails.
 */
export async function revokeSession(refreshToken, issuer) {
    if (!refreshToken)
        return;
    try {
        const config = await getConfig();
        const base = resolveIssuerBase(issuer, config);
        await axios.post(`${base}/session/revoke`, {
            refreshToken,
            appId: config.appId,
        }, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 15000,
            // Preserve the transport error for programmatic diagnostics while
            // ensuring AxiosError.toJSON() cannot serialize the live token body
            // into logs, telemetry, UI state, or crash reports.
            redact: ['data'],
        });
    }
    catch (err) {
        throw new SessionRevokeError(revokeFailureMessage(err), err);
    }
}
export async function signSessionCreateMessage(_signMessageFunction) {
}
